import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../api/client';
import {
  User,
  Mail,
  Shield,
  Settings,
  LogOut,
  CreditCard
} from 'lucide-react';

import { TabType, ProfileEmailAccount } from '../components/profile/types';
import ProfileSidebar from '../components/profile/ProfileSidebar';
import AccountInfoTab from '../components/profile/AccountInfoTab';
import EmailAccountsTab from '../components/profile/EmailAccountsTab';
import SecurityTab from '../components/profile/SecurityTab';
import AppSettingsTab from '../components/profile/AppSettingsTab';
import SubscriptionTab from '../components/profile/SubscriptionTab';

export default function Profile() {
  const { user, logout } = useAuth();
  const [emailAccounts, setEmailAccounts] = useState<ProfileEmailAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('account');
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const hasLoadedRef = useRef(false);

  useEffect(() => {
    // Prevent double fetch in React.StrictMode (development)
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    loadEmailAccounts();
  }, []);

  const loadEmailAccounts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await apiClient.get('/email-accounts', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setEmailAccounts(response.data.data);
      }
    } catch (error) {
      // Email hesapları yüklenirken hata oluştu
    } finally {
      setLoading(false);
    }
  };

  const deleteEmailAccount = async (accountId: string) => {
    if (!window.confirm('Bu email hesabını silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await apiClient.delete(`/email-accounts/${accountId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        await loadEmailAccounts();
      }
    } catch (error: any) {
      // Email hesabı silinemedi
    }
  };

  const toggleEmailAccountStatus = async (accountId: string) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await apiClient.put(`/email-accounts/${accountId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        await loadEmailAccounts();
      }
    } catch (error: any) {
      // Email hesabı durumu güncellenemedi
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordError('');
    setPasswordSuccess('');

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('Yeni şifreler eşleşmiyor');
      setPasswordLoading(false);
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordError('Şifre en az 6 karakter olmalıdır');
      setPasswordLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      // Note: Endpoint depends on backend implementation, usually /auth/change-password
      const response = await apiClient.post('/auth/change-password', {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setPasswordSuccess('Şifre başarıyla değiştirildi');
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      }
    } catch (error: any) {
      setPasswordError(error.response?.data?.error?.message || 'Şifre değiştirilemedi');
    } finally {
      setPasswordLoading(false);
    }
  };

  const tabs: TabType[] = [
    { id: 'account', name: 'Hesap Bilgileri', icon: User, color: 'blue' },
    { id: 'subscription', name: 'Abonelik', icon: CreditCard, color: 'cyan' },
    { id: 'emails', name: 'Kayıtlı Emailler', icon: Mail, color: 'green' },
    { id: 'security', name: 'Güvenlik', icon: Shield, color: 'red' },
    { id: 'settings', name: 'Ayarlar', icon: Settings, color: 'purple' }
  ];

  const handlePasswordFormChange = (field: string, value: string) => {
    setPasswordForm(prev => ({ ...prev, [field]: value }));
  };

  const handleToggleShowPassword = (field: string) => {
    setShowPasswords(prev => ({ ...prev, [field as keyof typeof showPasswords]: !prev[field as keyof typeof showPasswords] }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Profil Ayarları</h1>
              <p className="mt-2 text-gray-600">
                Hesap bilgilerinizi ve ayarlarınızı yönetin
              </p>
            </div>
            <button
              onClick={logout}
              className="flex items-center px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Çıkış Yap
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <ProfileSidebar
              user={user}
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 min-h-[500px]">
              <div className="p-8">
                {activeTab === 'account' && (
                  <AccountInfoTab user={user} />
                )}

                {activeTab === 'subscription' && (
                  <SubscriptionTab />
                )}

                {activeTab === 'emails' && (
                  <EmailAccountsTab
                    loading={loading}
                    emailAccounts={emailAccounts}
                    onAddAccount={() => window.location.href = '/email-accounts'}
                    onToggleStatus={toggleEmailAccountStatus}
                    onDeleteAccount={deleteEmailAccount}
                  />
                )}

                {activeTab === 'security' && (
                  <SecurityTab
                    form={passwordForm}
                    onFormChange={handlePasswordFormChange}
                    onSubmit={handlePasswordChange}
                    loading={passwordLoading}
                    error={passwordError}
                    onDismissError={() => setPasswordError('')}
                    success={passwordSuccess}
                    onDismissSuccess={() => setPasswordSuccess('')}
                    showPasswords={showPasswords}
                    onToggleShowPassword={handleToggleShowPassword}
                  />
                )}

                {activeTab === 'settings' && (
                  <AppSettingsTab onLogout={logout} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
